import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  FormLabel,
  Row,
  Stack,
  Table,
} from "react-bootstrap";
import { LuBuilding2 } from "react-icons/lu";
import { toast } from "react-toastify";
import LoaderMu from "../../../components/common/LoaderMu";
import { CustomSelect } from "../../../components/common/CustomSelect";
import {
  deletePropertyTypeMappingApi,
  fetchPropertyTypeMappingsApi,
  fetchSettingsPropertyTypesApi,
  savePropertyTypeMappingApi,
} from "../../../services/propertyType";
import { useSelector } from "react-redux";
import NotAuthorized from "../../../components/common/NotAuthorized";

const DEFAULT_FORM = {
  sourcePropertyTypeId: "",
  targetPropertyTypeId: "",
};

const getErrorMessage = (error) => {
  if (error?.status == 500) {
    return "Unauthorized or server issue. Please refresh or try later.";
  }
  return (
    error?.response?.data?.message ||
    error?.error_description ||
    error?.details ||
    error?.message ||
    "Something went wrong."
  );
};

export const Mappings = () => {
  const isAdmin = useSelector((state) =>
    state?.adminData?.authorities?.includes("ROLE_ADMIN")
  );

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [formValues, setFormValues] = useState(DEFAULT_FORM);

  const sourceTypeOptions = useMemo(
    () =>
      (propertyTypes || [])
        .filter((item) => item?.isAmakenNative === false)
        .map((item) => ({
          label: item?.title?.en,
          value: item?.id,
        })),
    [propertyTypes]
  );

  const targetTypeOptions = useMemo(
    () =>
      (propertyTypes || [])
        .filter((item) => item?.isAmakenNative === true)
        .map((item) => ({
          label: item?.title?.en,
          value: item?.id,
        })),
    [propertyTypes]
  );

  const fetchPageData = () => {
    setLoading(true);
    Promise.all([fetchPropertyTypeMappingsApi(), fetchSettingsPropertyTypesApi()])
      .then(([mappingsResponse, typesResponse]) => {
        setMappings(mappingsResponse || []);
        setPropertyTypes(typesResponse || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(getErrorMessage(error));
      });
  };

  const handleSaveMapping = () => {
    if (!formValues?.sourcePropertyTypeId || !formValues?.targetPropertyTypeId) {
      toast.error("Please select source and target property types.");
      return;
    }

    setIsSubmitting(true);
    savePropertyTypeMappingApi({
      sourcePropertyTypeId: Number(formValues?.sourcePropertyTypeId),
      targetPropertyTypeId: Number(formValues?.targetPropertyTypeId),
    })
      .then((response) => {
        setIsSubmitting(false);
        setFormValues(DEFAULT_FORM);
        fetchPageData();
        toast.success(response?.message || "Mapping saved successfully.");
      })
      .catch((error) => {
        setIsSubmitting(false);
        toast.error(getErrorMessage(error));
      });
  };

  const handleEdit = (mapping) => {
    setFormValues({
      sourcePropertyTypeId: mapping?.sourcePropertyTypeId || "",
      targetPropertyTypeId: mapping?.targetPropertyTypeId || "",
    });
  };

  const handleDelete = (mappingId) => {
    const shouldDelete = window.confirm(
      "Are you sure you want to delete this property type mapping?"
    );
    if (!shouldDelete) return;

    setLoading(true);
    deletePropertyTypeMappingApi(mappingId)
      .then(() => {
        fetchPageData();
        toast.success("Mapping deleted successfully.");
      })
      .catch((error) => {
        setLoading(false);
        toast.error(getErrorMessage(error));
      });
  };

  useEffect(() => {
    if (isAdmin) {
      fetchPageData();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  return (
    <div className="d-flex flex-column pageContainer p-3 h-100">
      <LoaderMu loading={loading} />
      <div className="flex-grow-1 h-100 pageContent position-relative pt-4 overflow-auto">
        {!isAdmin ? (
          <NotAuthorized />
        ) : (
          <Card className="h-100 bg-white shadow border-0 theme-card-cover">
            <div className="theme-card-header px-1">
              <Stack
                direction="horizontal"
                gap={2}
                className="flex-wrap justify-content-between px-3 pt-3 pb-2 align-items-start"
              >
                <h5 className="mb-0 position-relative fs-18 text-black fw-medium">
                  <div className="align-items-center bg-primary d-inline-flex h-60 justify-content-center position-absolute rounded start-0 text-white theme-icon-box w-60 z-1">
                    <LuBuilding2 size={40} />
                  </div>
                  Property Type Mappings
                </h5>
              </Stack>
            </div>

            <div className="flex-grow-1 d-flex flex-column px-3 h-100 overflow-auto">
              <Card className="border-0 shadow-none mt-3">
                <Card.Body className="px-1 pt-1">
                  <Row className="gx-xl-5">
                    <Col sm={12} md={6} lg={5} className="mb-4 pb-1">
                      <CustomSelect
                        options={sourceTypeOptions}
                        label="Source Property Type (Non-Native)"
                        value={formValues?.sourcePropertyTypeId}
                        name="sourcePropertyTypeId"
                        variant="outlined"
                        onChange={(event) =>
                          setFormValues((prev) => ({
                            ...prev,
                            sourcePropertyTypeId: event?.target?.value,
                          }))
                        }
                      />
                    </Col>
                    <Col sm={12} md={6} lg={5} className="mb-4 pb-1">
                      <CustomSelect
                        options={targetTypeOptions}
                        label="Target Property Type (Native)"
                        value={formValues?.targetPropertyTypeId}
                        name="targetPropertyTypeId"
                        variant="outlined"
                        onChange={(event) =>
                          setFormValues((prev) => ({
                            ...prev,
                            targetPropertyTypeId: event?.target?.value,
                          }))
                        }
                      />
                    </Col>
                    <Col sm={12} md={12} lg={2} className="mb-4 pb-1">
                      <FormLabel className="fs-6 fw-500 d-block opacity-0">
                        Save
                      </FormLabel>
                      <Button
                        className="w-100"
                        onClick={handleSaveMapping}
                        disabled={isSubmitting}
                      >
                        Save
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <div className="table-responsive pb-3">
                <Table bordered hover>
                  <thead>
                    <tr>
                      <th style={{ width: "80px" }}>#</th>
                      <th>Source Type (EN)</th>
                      <th>Source Type (AR)</th>
                      <th>Target Type (EN)</th>
                      <th>Target Type (AR)</th>
                      <th style={{ width: "170px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings?.length > 0 ? (
                      mappings.map((mapping, index) => (
                        <tr key={mapping?.id}>
                          <td>{index + 1}</td>
                          <td>{mapping?.sourceTypeNameEn || "-"}</td>
                          <td>{mapping?.sourceTypeNameAr || "-"}</td>
                          <td>{mapping?.targetTypeNameEn || "-"}</td>
                          <td>{mapping?.targetTypeNameAr || "-"}</td>
                          <td>
                            <Stack direction="horizontal" gap={2}>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEdit(mapping)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(mapping?.id)}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="text-center py-4" colSpan={6}>
                          No property type mappings found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
